// src/components/warranties/SellerWarrantiesAccordion.jsx
import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";

import { djangoApi } from "../../utils/axios/axios.utils";

function fmtDateBR(iso) {
    const s = String(iso || "").slice(0, 10);
    if (!s) return "-";
    const [y, m, d] = s.split("-");
    if (!y || !m || !d) return s;
    return `${d}/${m}/${y}`;
}

function statusLabel(status) {
    const s = String(status || "").toUpperCase();
    const map = {
        OPEN: "ABERTA",
        PENDING: "PENDENTE",
        ATFACTORY: "NA FÁBRICA",
        SENDTOFACTORY: "ENVIAR PARA FÁBRICA",
        RETURNSELLER: "DEVOLVER P/ REVENDEDORA",
        SOLVED: "RESOLVIDO",
        CANCELED: "ANULADO",
    };
    return map[s] || s || "-";
}

// ✅ alinhado com seu Warranty.Status (TextChoices)
const STATUS_OPTIONS = [
    { value: "OPEN", label: "ABERTA" },
    { value: "PENDING", label: "PENDENTE" },
    { value: "ATFACTORY", label: "NA FÁBRICA" },
    { value: "SENDTOFACTORY", label: "ENVIAR PARA FÁBRICA" },
    { value: "RETURNSELLER", label: "DEVOLVER PARA REVENDEDORA" },
    { value: "SOLVED", label: "RESOLVIDO" },
    { value: "CANCELED", label: "ANULADO" },
];

export default function SellerWarrantiesAccordion({
    seller,
    theme,
    colors,
    subtleBorder,
    softText,
}) {
    const sellerId = seller?.id;

    const [expanded, setExpanded] = useState(false);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadedOnce, setLoadedOnce] = useState(false);
    const [error, setError] = useState("");

    // saving por linha (para UX)
    const [saving, setSaving] = useState({}); // { [warrantyId]: true }

    // modal edição
    const [editOpen, setEditOpen] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editItem, setEditItem] = useState(null);

    const [editForm, setEditForm] = useState({
        received_at: "",
        status: "OPEN",
        lot_code: "",
        customer_contact: "",
        product_code: "",
        sale_month: "",
        product_description: "",
        problem_reported: "",

        // ✅ Base.observacao = "Solução"
        observacao: "",

        // ✅ notes (extra do Warranty) opcional
        notes: "",
    });

    const headers = useMemo(
        () => ({
            Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
            "Content-Type": "application/json",
        }),
        []
    );

    const fetchSellerWarranties = useCallback(async () => {
        if (!sellerId) {
            setRows([]);
            setLoadedOnce(false);
            setError("");
            return;
        }

        try {
            setLoading(true);
            setError("");

            const res = await djangoApi.get("products/warranties/", {
                params: { seller_id: sellerId },
                headers,
            });

            const data = res?.data;
            const list = Array.isArray(data)
                ? data
                : Array.isArray(data?.results)
                    ? data.results
                    : [];

            setRows(list);
            setLoadedOnce(true);
        } catch (err) {
            console.log("Erro ao listar garantias do seller", err);
            setRows([]);
            setLoadedOnce(true);
            setError("Falha ao carregar garantias. Veja o console.");
        } finally {
            setLoading(false);
        }
    }, [sellerId, headers]);

    const handleToggleAccordion = async (_, isExpanded) => {
        setExpanded(isExpanded);

        // ✅ ao abrir pela primeira vez, carrega
        if (isExpanded && !loadedOnce) {
            await fetchSellerWarranties();
        }
    };

    // ✅ quando trocar seller, reseta e recarrega (se o accordion já estiver aberto)
    useEffect(() => {
        setRows([]);
        setLoadedOnce(false);
        setError("");
        if (sellerId) fetchSellerWarranties();
    }, [sellerId]); // eslint-disable-line react-hooks/exhaustive-deps

    const patchWarranty = async (id, payload) => {
        return djangoApi.patch(`products/warranties/${id}/`, payload, { headers });
    };

    const handleToggleBool = async (w, field) => {
        const id = w?.id || w?.pk;
        if (!id) return;

        const next = !Boolean(w?.[field]);

        // ✅ optimistic UI
        setSaving((p) => ({ ...p, [id]: true }));
        setRows((prev) =>
            prev.map((r) => ((r.id || r.pk) === id ? { ...r, [field]: next } : r))
        );

        try {
            await patchWarranty(id, { [field]: next });
        } catch (err) {
            console.log("Erro ao atualizar boolean", err);

            // rollback
            setRows((prev) =>
                prev.map((r) =>
                    (r.id || r.pk) === id ? { ...r, [field]: !next } : r
                )
            );

            setError("Falha ao salvar alteração (rollback aplicado).");
        } finally {
            setSaving((p) => ({ ...p, [id]: false }));
        }
    };

    const openEditModal = (w) => {
        const id = w?.id || w?.pk;
        if (!id) return;

        setError("");
        setEditItem(w);

        setEditForm({
            received_at: String(w.received_at || "").slice(0, 10) || "",
            status: String(w.status || "OPEN").toUpperCase(),
            lot_code: w.lot_code || "",
            customer_contact: w.customer_contact || "",
            product_code: w.product_code || "",
            sale_month: w.sale_month || "",
            product_description: w.product_description || "",
            problem_reported: w.problem_reported || "",

            // ✅ Base.observacao
            observacao: w.observacao || "",

            // ✅ Warranty.notes
            notes: w.notes || "",
        });

        setEditOpen(true);
    };

    const closeEditModal = () => {
        if (editSaving) return;
        setEditOpen(false);
        setEditItem(null);
    };

    const handleSaveEdit = async () => {
        const id = editItem?.id || editItem?.pk;
        if (!id) return;

        setEditSaving(true);
        setError("");

        const payload = {
            received_at: editForm.received_at || null,
            status: String(editForm.status || "OPEN").toUpperCase(),

            // ✅ lote opcional
            lot_code: String(editForm.lot_code || "").trim() || null,

            customer_contact: String(editForm.customer_contact || "").trim() || null,
            product_code: String(editForm.product_code || "").trim() || null,
            sale_month: String(editForm.sale_month || "").trim() || null,
            product_description: String(editForm.product_description || "").trim() || null,
            problem_reported: String(editForm.problem_reported || "").trim() || null,

            // ✅ Solução
            observacao: String(editForm.observacao || "").trim() || null,

            // ✅ notes (opcional)
            notes: String(editForm.notes || "").trim() || null,
        };

        try {
            const res = await patchWarranty(id, payload);
            const updated = res?.data || payload;

            setRows((prev) =>
                prev.map((r) => ((r.id || r.pk) === id ? { ...r, ...updated } : r))
            );

            setEditOpen(false);
            setEditItem(null);
        } catch (err) {
            console.log("Erro ao salvar edição", err?.response?.data || err);
            const data = err?.response?.data;
            const detail =
                data?.detail ||
                (data && typeof data === "object"
                    ? Object.entries(data)
                        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`)
                        .join(" | ")
                    : null) ||
                "Falha ao salvar edição. Veja o console.";
            setError(detail);
        } finally {
            setEditSaving(false);
        }
    };

    const headerRight = (
        <Stack direction="row" spacing={1} alignItems="center">
            {loading && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="body2" color={softText}>
                        Carregando…
                    </Typography>
                </Stack>
            )}

            <Chip
                size="small"
                variant="outlined"
                label={
                    sellerId
                        ? `${rows.length} garantia${rows.length === 1 ? "" : "s"}`
                        : "sem vendedora"
                }
            />

            <Tooltip title="Recarregar">
                <span>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            fetchSellerWarranties();
                        }}
                        disabled={!sellerId || loading}
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </Stack>
    );

    return (
        <Paper
            elevation={8}
            sx={{
                backgroundColor: colors?.pink?.[900],
                borderRadius: 2,
                border: subtleBorder,
            }}
        >
            <Accordion
                expanded={expanded}
                onChange={handleToggleAccordion}
                disableGutters
                sx={{
                    bgcolor: "transparent",
                    color: "inherit",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: "100%" }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <VerifiedUserIcon fontSize="small" />
                            <Typography fontWeight={900}>Garantias</Typography>
                            {!!seller?.nome && (
                                <Chip size="small" variant="outlined" label={seller.nome} />
                            )}
                        </Stack>
                        {headerRight}
                    </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2, pb: 2 }}>
                    <Divider sx={{ mb: 2 }} />

                    {!sellerId ? (
                        <Typography variant="body2" color={softText}>
                            Selecione uma vendedora para carregar as garantias.
                        </Typography>
                    ) : error ? (
                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                            {error}
                        </Typography>
                    ) : !loading && rows.length === 0 ? (
                        <Typography variant="body2" color={softText}>
                            Nenhuma garantia encontrada para esta vendedora.
                        </Typography>
                    ) : (
                        <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                border: subtleBorder,
                                bgcolor: theme?.palette?.mode === "dark" ? colors?.pink?.[800] : "#fff",
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Nº</b></TableCell>
                                        <TableCell><b>Receb.</b></TableCell>
                                        <TableCell><b>Status</b></TableCell>
                                        <TableCell><b>KitProduct</b></TableCell>

                                        <TableCell align="center"><b>Garantia?</b></TableCell>
                                        <TableCell align="center"><b>Fábrica?</b></TableCell>
                                        <TableCell align="center"><b>Conserto?</b></TableCell>

                                        <TableCell><b>Lote</b></TableCell>
                                        <TableCell><b>Problema</b></TableCell>
                                        <TableCell><b>Solução</b></TableCell>
                                        <TableCell align="center"><b>Editar</b></TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {rows.map((w) => {
                                        const id = w.id || w.pk;
                                        const isSaving = !!saving[id];

                                        return (
                                            <TableRow key={id} hover sx={{ opacity: isSaving ? 0.75 : 1 }}>
                                                <TableCell>
                                                    <Chip size="small" variant="outlined" label={w.number_ref || "-"} />
                                                </TableCell>

                                                <TableCell>{fmtDateBR(w.received_at)}</TableCell>

                                                <TableCell>
                                                    <Chip size="small" variant="outlined" label={statusLabel(w.status)} />
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2">
                                                        #{w.kit_product || w.kit_product_id || w.kit_product_pk || "-"}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Switch
                                                        size="small"
                                                        checked={!!w.is_warranty}
                                                        disabled={isSaving}
                                                        onChange={() => handleToggleBool(w, "is_warranty")}
                                                        color="success"
                                                    />
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Switch
                                                        size="small"
                                                        checked={!!w.sent_to_factory}
                                                        disabled={isSaving}
                                                        onChange={() => handleToggleBool(w, "sent_to_factory")}
                                                        color="warning"
                                                    />
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Switch
                                                        size="small"
                                                        checked={!!w.internal_repair}
                                                        disabled={isSaving}
                                                        onChange={() => handleToggleBool(w, "internal_repair")}
                                                        color="info"
                                                    />
                                                </TableCell>

                                                <TableCell>{w.lot_code || "-"}</TableCell>

                                                <TableCell>
                                                    <Typography variant="body2" sx={{ maxWidth: 240 }} noWrap title={w.problem_reported || ""}>
                                                        {w.problem_reported || "-"}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2" sx={{ maxWidth: 240 }} noWrap title={w.observacao || ""}>
                                                        {w.observacao || "-"}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Tooltip title="Editar detalhes">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openEditModal(w)}
                                                                disabled={isSaving}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>

                            {loading && (
                                <Box sx={{ p: 2 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={18} />
                                        <Typography variant="body2" color={softText}>
                                            Atualizando tabela…
                                        </Typography>
                                    </Stack>
                                </Box>
                            )}
                        </TableContainer>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ===== Modal edição ===== */}
            <Dialog open={editOpen} onClose={closeEditModal} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontWeight: 900 }}>
                    Editar Garantia #{editItem?.number_ref || editItem?.id || editItem?.pk || ""}
                </DialogTitle>

                <DialogContent dividers>
                    {error && (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}

                    <Stack spacing={2}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Data de recebimento"
                                value={editForm.received_at}
                                onChange={(e) => setEditForm((p) => ({ ...p, received_at: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />

                            <TextField
                                select
                                fullWidth
                                label="Status"
                                value={editForm.status}
                                onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                            >
                                {STATUS_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                fullWidth
                                label="Lote (opcional)"
                                value={editForm.lot_code}
                                onChange={(e) => setEditForm((p) => ({ ...p, lot_code: e.target.value }))}
                                placeholder="Pode preencher depois"
                            />
                        </Stack>

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                fullWidth
                                disabled
                                label="Código"
                                value={editForm.product_code}
                                onChange={(e) => setEditForm((p) => ({ ...p, product_code: e.target.value }))}
                            />

                            <TextField
                                fullWidth
                                disabled
                                label="Mês de venda"
                                value={editForm.sale_month}
                                onChange={(e) => setEditForm((p) => ({ ...p, sale_month: e.target.value }))}
                                // helperText="Ex: jan/2026 (ou o padrão que você estiver usando)"
                            />
                        </Stack>

                        <TextField
                            fullWidth
                            label="Descrição do produto"
                            value={editForm.product_description}
                            onChange={(e) => setEditForm((p) => ({ ...p, product_description: e.target.value }))}
                        />

                        <TextField
                            fullWidth
                            label="Problema apresentado"
                            value={editForm.problem_reported}
                            onChange={(e) => setEditForm((p) => ({ ...p, problem_reported: e.target.value }))}
                            multiline
                            minRows={3}
                        />

                        {/* ✅ Base.observacao */}
                        <TextField
                            fullWidth
                            label="Solução"
                            value={editForm.observacao}
                            onChange={(e) => setEditForm((p) => ({ ...p, observacao: e.target.value }))}
                            multiline
                            minRows={2}
                            placeholder="Descreva a solução aplicada / conclusão"
                        />

                        {/* ✅ notes (se quiser manter) */}
                        <TextField
                            fullWidth
                            label="Notas internas (opcional)"
                            value={editForm.notes}
                            onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                            multiline
                            minRows={2}
                            placeholder="Observações internas da equipe"
                        />
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeEditModal} disabled={editSaving} variant="outlined" color="warning">
                        Cancelar
                    </Button>

                    <Button onClick={handleSaveEdit} disabled={editSaving} variant="contained" color="success">
                        {editSaving ? "Salvando..." : "Salvar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
